import { useMemo } from "react";
import intl from "react-intl-universal";
import {
  TbActivity,
  TbAlertTriangle,
  TbCircleCheck,
  TbClockBolt,
  TbServerBolt,
  TbTrendingUp,
} from "react-icons/tb";
import { SummaryCard } from "../../../../../Components/Atoms/SummaryCard";
import { mapBaasApiOverviewMetrics } from "../../../../../Services/baasApiMetricsApi";

const HEALTH_STYLES = {
  healthy: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  degraded: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  down: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300",
};

const formatCount = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value);

const formatPercent = (value, fractionDigits = 2) =>
  `${new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)}%`;

const formatMs = (value) => `${formatCount(value)} ms`;

/**
 * Observed availability from real request totals (not a contractual SLA).
 * (2xx + 4xx) / total — 5xx count against availability.
 */
const computeAvailability = (metrics) => {
  const total = Number(metrics.totalRequests) || 0;
  if (total <= 0) return 100;
  const serverErrors = Number(metrics.statusBreakdown?.serverError5xx) || 0;
  return Math.max(0, Math.min(100, ((total - serverErrors) / total) * 100));
};

export const BaasApiSlaPanel = ({
  filters,
  metricsData = null,
  metricsLoading = false,
}) => {
  const metrics = useMemo(
    () => mapBaasApiOverviewMetrics(metricsData),
    [metricsData],
  );

  const availability = useMemo(() => computeAvailability(metrics), [metrics]);
  const healthStatus = metrics.healthSummary?.status ?? metrics.healthStatus ?? "healthy";
  const periodLabel = intl.get(`baas.api.period.${filters?.period || "24h"}`);

  const cards = useMemo(
    () => [
      {
        key: "availability",
        icon: TbCircleCheck,
        title: intl.get("baas.api.sla.availability"),
        value: formatPercent(availability),
        footerLabel: intl.get("baas.api.sla.availabilitySub"),
      },
      {
        key: "successRate",
        icon: TbTrendingUp,
        title: intl.get("baas.api.sla.successRate"),
        value: formatPercent(metrics.successRate, 1),
        footerLabel: intl.get("baas.api.sla.successRateSub"),
      },
      {
        key: "errorRate",
        icon: TbAlertTriangle,
        title: intl.get("baas.api.sla.errorRate"),
        value: formatPercent(metrics.errorRate, 1),
        footerLabel: intl.get("baas.api.sla.errorRateSub"),
      },
      {
        key: "avgLatency",
        icon: TbClockBolt,
        title: intl.get("baas.api.sla.avgLatency"),
        value: formatMs(metrics.avgLatencyMs),
        footerLabel: intl.get("baas.api.sla.avgLatencySub"),
      },
      {
        key: "totalRequests",
        icon: TbActivity,
        title: intl.get("baas.api.sla.totalRequests"),
        value: formatCount(metrics.totalRequests),
        footerLabel: periodLabel,
      },
      {
        key: "serverErrors",
        icon: TbServerBolt,
        title: intl.get("baas.api.sla.serverErrors"),
        value: formatCount(metrics.statusBreakdown.serverError5xx),
        footerLabel: intl.get("baas.api.sla.serverErrorsSub"),
      },
    ],
    [availability, metrics, periodLabel],
  );

  if (metricsLoading) {
    return (
      <div className="rounded-lg border border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
        {intl.get("baas.api.sla.loading")}
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-5">
      <div>
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.api.sla.sectionTitle")}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.api.sla.sectionSubtitle")}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {intl.get("baas.api.sla.observedStatus")}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
              HEALTH_STYLES[healthStatus] ?? HEALTH_STYLES.healthy
            }`}
          >
            <TbCircleCheck className="h-3.5 w-3.5" />
            {intl.get(`baas.api.health.${healthStatus}`)}
          </span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.api.sla.periodNote", { period: periodLabel })}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => (
          <SummaryCard
            key={card.key}
            icon={card.icon}
            title={card.title}
            value={card.value}
            footerLabel={card.footerLabel}
          />
        ))}
      </div>

      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-3 text-xs text-gray-500 dark:border-brand-dark-border dark:text-gray-400">
        {intl.get("baas.api.sla.disclaimer")}
      </div>
    </div>
  );
};
